import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const COLORS = ["#E50914","#F59E0B","#22C55E","#3B82F6","#A1A1AA","#71717A","#FF3B30"];

export default function PayrollReports() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [bonuses, setBonuses] = useState([]);

  useEffect(() => {
    api.get(`/payroll-reports/summary?month=${month}&year=${year}`).then((r) => setSummary(r.data)).catch(()=>{});
    api.get(`/payroll-reports/deductions?month=${month}&year=${year}`).then((r) => setDeductions(r.data)).catch(()=>{});
    api.get(`/payroll-reports/bonuses?month=${month}&year=${year}`).then((r) => setBonuses(r.data)).catch(()=>{});
  }, [month, year]);

  const deptData = summary ? Object.entries(summary.by_department).map(([k, v]) => ({ name: k, net: v.net, count: v.count })) : [];
  const statusData = summary ? Object.entries(summary.by_status).map(([k, v]) => ({ name: k, value: v })) : [];

  const dedCatTotals = Object.values(deductions.reduce((acc, d) => {
    acc[d.category] = acc[d.category] || { name: d.category, value: 0 };
    acc[d.category].value += Number(d.amount || 0);
    return acc;
  }, {}));
  const bonCatTotals = Object.values(bonuses.reduce((acc, b) => {
    acc[b.type] = acc[b.type] || { name: b.type, value: 0 };
    acc[b.type].value += Number(b.amount || 0);
    return acc;
  }, {}));

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Reports</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Payroll Reports</h1>
        </div>
        <div className="flex gap-3">
          <Select value={String(month)} onValueChange={(v)=>setMonth(Number(v))}>
            <SelectTrigger className="w-28 bg-[#16161C] border-[#2D2D35] h-10" data-testid="pr-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v)=>setYear(Number(v))}>
            <SelectTrigger className="w-24 bg-[#16161C] border-[#2D2D35] h-10" data-testid="pr-year"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              {[year-1, year, year+1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Stat label="Total Slips"      v={summary.count} testid="pr-stat-count" />
          <Stat label="Att. Salary"      v={`₹ ${summary.total_attendance_salary.toLocaleString()}`} testid="pr-stat-att-salary" />
          <Stat label="Overtime"         v={`₹ ${summary.total_overtime.toLocaleString()}`} color="#F59E0B" testid="pr-stat-overtime" />
          <Stat label="Bonuses"          v={`₹ ${summary.total_bonus.toLocaleString()}`} color="#22C55E" testid="pr-stat-bonuses" />
          <Stat label="Net Payout"       v={`₹ ${summary.total_net.toLocaleString()}`} color="#E50914" testid="pr-stat-net" />
        </div>
      )}

      <Tabs defaultValue="dept">
        <TabsList className="bg-[#16161C] border border-[#2D2D35]">
          <TabsTrigger value="dept" data-testid="pr-tab-department">Department</TabsTrigger>
          <TabsTrigger value="status" data-testid="pr-tab-status">Status</TabsTrigger>
          <TabsTrigger value="ded" data-testid="pr-tab-deductions">Deductions</TabsTrigger>
          <TabsTrigger value="bon" data-testid="pr-tab-bonuses">Bonuses</TabsTrigger>
        </TabsList>

        <TabsContent value="dept" className="mt-5">
          <Card title="Net Payout by Department">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={deptData}>
                <CartesianGrid stroke="#2D2D35" vertical={false} />
                <XAxis dataKey="name" stroke="#A1A1AA" fontSize={11} />
                <YAxis stroke="#A1A1AA" fontSize={11} />
                <Tooltip contentStyle={{ background: "#16161C", border: "1px solid #2D2D35", color: "#fff" }} />
                <Bar dataKey="net" fill="#E50914" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="status" className="mt-5">
          <Card title="Slips by Workflow Status">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: "#16161C", border: "1px solid #2D2D35", color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="ded" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Deduction Categories">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={dedCatTotals} layout="vertical">
                  <CartesianGrid stroke="#2D2D35" horizontal={false} />
                  <XAxis type="number" stroke="#A1A1AA" fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke="#A1A1AA" fontSize={10} width={120} />
                  <Tooltip contentStyle={{ background: "#16161C", border: "1px solid #2D2D35", color: "#fff" }} />
                  <Bar dataKey="value" fill="#FF3B30" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Recent Deductions">
              <DataTable rows={deductions} cols={["employee_name","category","reason","amount"]} />
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="bon" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Bonus Categories">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={bonCatTotals} layout="vertical">
                  <CartesianGrid stroke="#2D2D35" horizontal={false} />
                  <XAxis type="number" stroke="#A1A1AA" fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke="#A1A1AA" fontSize={10} width={120} />
                  <Tooltip contentStyle={{ background: "#16161C", border: "1px solid #2D2D35", color: "#fff" }} />
                  <Bar dataKey="value" fill="#22C55E" radius={[0,4,4,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title="Recent Bonuses">
              <DataTable rows={bonuses} cols={["employee_name","type","reason","amount"]} />
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, v, color = "#FFFFFF", testid }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-xl mt-2" style={{ color }}>{v}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded-md">
      <div className="px-5 py-4 border-b border-[#2D2D35] text-sm font-medium tracking-wide">{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}
function DataTable({ rows, cols }) {
  if (!rows?.length) return <div className="text-xs text-zinc-500 p-3">No records.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
          {cols.map((c) => <th key={c} className="text-left p-2">{c.replace("_"," ")}</th>)}
        </tr></thead>
        <tbody>
          {rows.slice(0, 25).map((r, i) => (
            <tr key={i} className="border-b border-[#2D2D35]/40">
              {cols.map((c) => <td key={c} className={`p-2 ${c==="amount" ? "text-right font-mono" : ""}`}>{c==="amount" ? `₹ ${Number(r[c]||0).toLocaleString()}` : (r[c] || "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
